import Extension from "../base/extension";

function parseOptions(node) {
  try {
    return JSON.parse(node.textContent || "{}");
  } catch {
    return null;
  }
}

/**
 * ProductPhotoswipe
 *
 * Opens the PhotoSwipe lightbox when a product media slide is clicked.
 *
 * Delegated from document rather than bound per container at init: the gallery
 * can arrive long after page load (quick view injects a whole product section,
 * the theme editor re-renders sections), and the previous per-container binding
 * ran once at init, so an injected gallery was never wired and clicking a
 * quick-view image did nothing.
 *
 * Scoping matters because quick view injects a copy of the PDP's product-main
 * markup, ids included: two `#product-media-<sectionId>-full` containers and two
 * payload scripts with the same `data-section-id` can coexist. The payload is
 * therefore looked up within the clicked gallery's own subtree first, so the
 * quick view uses its own data source rather than the underlying page's.
 */
class ProductPhotoswipe extends Extension {
  name = "ProductPhotoswipe";
  static autoregister = true;

  init() {
    document.addEventListener("click", this.#onClick);
  }

  #onClick = async (event) => {
    const slide = event.target.closest(".swiper-slide");
    if (!slide) return;

    const container = slide.closest('[id^="product-media-"][id$="-full"]');
    if (!container) return;
    if ("pswpIgnore" in slide.dataset) return;

    const sectionId = container.id.replace(/^product-media-/, "").replace(/-full$/, "");
    const payload = this.#findPayload(container, sectionId);
    if (!payload) return;

    const options = parseOptions(payload);
    if (!options?.dataSource?.length) return;

    event.preventDefault();

    const idx = parseInt(slide.dataset.swiperSlideIndex, 10);
    options.index = Number.isFinite(idx) ? idx : 0;

    // A gallery inside a modal <dialog> needs the lightbox appended INTO that
    // dialog. PhotoSwipe defaults to document.body, and a dialog opened with
    // showModal() sits in the top layer, which paints above all normal content
    // whatever its z-index: the lightbox opened but was completely hidden
    // behind the quick view. The dialog is not a containing block for fixed
    // positioning (no transform/filter/contain), so .pswp still covers the
    // viewport rather than being clipped to the dialog's box.
    const owningDialog = container.closest("dialog");
    if (owningDialog?.open) options.appendToEl = owningDialog;

    const PhotoSwipe = await window.Stencil.ensureVendor("photoswipe");
    const pswp = new PhotoSwipe(options);
    pswp.init();
  };

  /**
   * Prefer a payload inside the same rendering root as the clicked gallery
   * (quick view dialog, or the owning section), falling back to the document so
   * a plain PDP still resolves.
   */
  #findPayload(container, sectionId) {
    const selector = `script[data-pswp-datasource][data-section-id="${sectionId}"]`;
    const roots = [
      container.closest("quick-view"),
      container.closest("dialog"),
      container.closest(".shopify-section"),
    ].filter(Boolean);

    for (const root of roots) {
      const scoped = root.querySelector(selector) || root.querySelector("script[data-pswp-datasource]");
      if (scoped) return scoped;
    }
    return document.querySelector(selector);
  }
}

export default ProductPhotoswipe;
